<template>
    <DCard
        padding
        :title="$t('settings_hosting_platforms.create_title')"
    >
        <DFormRow
            :label="$t('settings_hosting_platforms.type')"
            is-required
            :error="errors.type"
        >
            <DRadioGroup
                id="hosting_platform_type"
                v-model="form.type"
                :placeholder="$t('settings_hosting_platforms.type')"
                :options="hostingPlatformOptions"
            />
        </DFormRow>
        <DFormRow
            :label="$t('settings_hosting_platforms.hostname')"
            is-required
            :error="form.errors.hostname"
        >
            <DInput
                id="hosting_platform_hostname"
                v-model="form.hostname"
                :placeholder="$t('settings_hosting_platforms.hostname')"
            />
        </DFormRow>
        <DFormRow
            v-show="form.type === 'direct_admin'"
            :label="$t('settings_hosting_platforms.username')"
            is-required
            :error="form.errors.username"
        >
            <DInput
                id="hosting_platform_username"
                v-model="form.username"
                :placeholder="$t('settings_hosting_platforms.username')"
                maxlength="128"
            />
        </DFormRow>
        <DFormRow
            v-show="form.type === 'direct_admin' || form.type === 'rundeck'"
            :label="form.type === 'rundeck' ? $t('settings_hosting_platforms.api_token') : $t('settings_hosting_platforms.password')"
            is-required
            :error="form.errors.password"
        >
            <DInput
                id="hosting_platform_password"
                v-model="form.password"
                type="password"
                :placeholder="form.type === 'rundeck' ? $t('settings_hosting_platforms.api_token') : $t('settings_hosting_platforms.password')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            v-show="form.type === 'rundeck'"
            :label="$t('settings_hosting_platforms.rundeck_job_id')"
            is-required
            :error="form.errors.rundeck_job_id"
        >
            <DInput
                id="hosting_platform_rundeck_job_id"
                v-model="form.rundeck_job_id"
                :placeholder="$t('settings_hosting_platforms.rundeck_job_id')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            v-show="form.type === 'rundeck'"
            :label="$t('settings_hosting_platforms.api_version')"
            is-required
            :error="form.errors.rundeck_api_version"
        >
            <DInput
                id="hosting_platform_rundeck_api_version"
                v-model="form.rundeck_api_version"
                :placeholder="$t('settings_hosting_platforms.api_version')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="$t('settings_hosting_platforms.ip_addresses')"
            is-required
            :hint="$t('settings_hosting_platforms.hint_ip_addresses')"
            :error="form.errors.ip_addresses"
        >
            <DFormTextarea
                id="hosting_platform_ip_addresses"
                v-model="form.ip_addresses"
                rows="10"
            />
        </DFormRow>
        <DRow
            v-show="form.type === 'direct_admin' || form.type === 'rundeck'"
        >
            <DCol sm="4" />
            <DCol sm="4">
                <div
                    v-show="connectionStatus === 'successful'"
                    class="green-text"
                >
                    {{ $t('common.connection_successful') }}
                </div>
                <div
                    v-show="connectionStatus === 'unsuccessful'"
                    class="red-text"
                >
                    {{ $t('common.connection_unsuccessful') }}
                </div>
                <div
                    v-show="connectionStatus === 'warning'"
                    class="warning-text"
                >
                    {{ warningMessage }}
                </div>
            </DCol>
            <DCol
                sm="4"
                class="check-database-connection-btn"
            >
                <DButton
                    icon="lan-connect"
                    :label="$t('common.check_connection')"
                    @click="checkConnection"
                />
            </DCol>
        </DRow>
    </DCard>
    <DFormControls
        :dirty="form.isDirty"
        @submit="submitForm"
    />
</template>

<script setup>
import { useForm } from '@inertiajs/vue3'
import axios from 'axios'
import { ref } from 'vue'

const props = defineProps({
    hostingPlatform: {
        type: Object,
        default () {
            return {}
        }
    },
    hostingPlatformOptions: {
        type: Array,
        default () {
            return []
        }
    },
    editPage: {
        type: Boolean,
        default: false
    }
})

const form = useForm({
    type: props.hostingPlatform.type ?? null,
    username: props.hostingPlatform.username ?? null,
    password: props.hostingPlatform.password ?? null,
    hostname: props.hostingPlatform.hostname ?? null,
    rundeck_job_id: props.hostingPlatform.rundeck_job_id ?? null,
    rundeck_api_version: props.hostingPlatform.rundeck_api_version ?? null,
    ip_addresses: props.hostingPlatform.ip_addresses ?? null
})

const submitForm = () => {
    if (props.editPage) {
        form.patch(route('settings.hosting-platforms.update', { hosting_platform: props.hostingPlatform.id }))
    } else {
        form.post(route('settings.hosting-platforms.store'))
    }
}

const connectionStatus = ref(null)
const warningMessage = ref(null)
const errors = ref({})

const checkConnection = function () {
    errors.value = {}
    axios.post(route('settings.hosting-platforms.check-connection', {
        username: form.username,
        password: form.password,
        hostname: form.hostname,
        type: form.type,
        rundeck_job_id: form.rundeck_job_id,
        rundeck_api_version: form.rundeck_api_version,
        ip_addresses: form.ip_addresses,
        is_check_connection: true
    }))
        .then(function (response) {
            if (response.data && response.data.warning) {
                warningMessage.value = response.data.warning
                connectionStatus.value = 'warning'
            } else {
                connectionStatus.value = 'successful'
            }
        })
        .catch(function (error) {
            errors.value = error.response.data.errors
            connectionStatus.value = 'unsuccessful'
        })
}
</script>

<style scoped lang="scss">
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 25px;
}

.green-text {
    color: green;
}
.red-text {
    color: red;
}

.warning-text {
    color: darkorange;
}

</style>
